'use client'

import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Container } from '@src/components/Container/Container'
import { type RailProps } from '@src/components/Rail/Rail.props'
import { RailCarouselArrows } from '@src/components/Rail/RailCarousel/RailCarouselArrows'
import { useRailArrowPosition } from '@src/components/Rail/RailCarousel/useRailArrowPosition'
import { RailItem } from '@src/components/Rail/RailItem'
import { useBreakpoints } from '@src/utils/breakpoints.util'
import { mouseWheelPlugin } from '@src/utils/slider.utils'
import { cn } from '@src/utils/tailwind'

type RailCarouselProps = Pick<
  RailProps,
  'items' | 'columnsDesktop' | 'columnsMobile' | 'imageStyle' | 'builderBlock' | 'slug'
> & {
  isPreviewing?: boolean
}

export const RailCarousel = (props: RailCarouselProps) => {
  const {
    items,
    columnsDesktop = 1,
    columnsMobile = 1,
    imageStyle,
    isPreviewing,
    builderBlock,
    slug,
  } = props

  const [isLoaded, setIsLoaded] = useState(false)
  const breakpoints = useBreakpoints()
  const isMobile = breakpoints.isMobile || breakpoints.isTablet

  const [hideLeft, setHideLeft] = useState(false)
  const [hideRight, setHideRight] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: false,
      mode: 'free',
      rubberband: false,
      created: () => {
        setIsLoaded(true)
      },
      slideChanged: (s) => {
        // reached start of carousel?
        setHideLeft(s.track.details.rel === 0)
        // reached end of carousel?
        setHideRight(s.track.details.maxIdx === s.track.details.rel)
      },
      slides: {
        perView: 'auto',
        origin: 'auto',
        spacing: 12,
      },
      breakpoints: {
        '(min-width: 1023px)': {
          slides: {
            perView: columnsDesktop,
            origin: 'auto',
            spacing: 32,
          },
        },
      },
    },
    [mouseWheelPlugin]
  )
  const arrowPosition = useRailArrowPosition(instanceRef, containerRef, setHideLeft, setHideRight)

  // Trigger a resize event to ensure the slider resizes correctly on server render
  useEffect(() => {
    const handleResize = () => {
      instanceRef.current?.update()
    }
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [instanceRef])

  const renderItems = useCallback(() => {
    return items.map(({ blocks }, index) => {
      const effectiveMobileColumns = columnsMobile * 1.25 // show an extra fraction of a column on mobile (peeking effect)
      // on mobile, we manually set the width of each slide so that the spacers can have different widths
      // on desktop, we let keen slider handle the width of each slide
      const width = isMobile ? `${100 / effectiveMobileColumns}%` : undefined

      return (
        <div
          key={index}
          className={cn('keen-slider__slide px-2 ![overflow:visible]', {
            'opacity-0': !isLoaded,
          })}
          // keen slider recommends setting the slide width like this
          style={{ minWidth: width, maxWidth: width }}
        >
          <RailItem
            index={index}
            blocks={blocks}
            imageStyle={imageStyle}
            isPreviewing={isPreviewing}
            builderBlock={builderBlock}
            columnsDesktop={columnsDesktop}
            slug={slug}
          />
        </div>
      )
    })
  }, [
    items,
    columnsMobile,
    isMobile,
    isLoaded,
    imageStyle,
    isPreviewing,
    builderBlock,
    columnsDesktop,
    slug,
  ])

  const renderMobileSpacer = () => {
    // on mobile the rail spans the full width of the screen
    // in order do add the horizontal margins required and to make sure
    // each item has the correct width, we need to add spacer items on the left and right

    if (!isMobile) return null

    return (
      <div className={cn('keen-slider__slide', 'min-w-1 max-w-1', 'sm:min-w-5 sm:max-w-5')}></div>
    )
  }

  const content = (
    <div className="relative" ref={containerRef}>
      {isLoaded && (
        <RailCarouselArrows
          arrowPosition={arrowPosition}
          previousSlide={() => instanceRef.current?.prev()}
          nextSlide={() => instanceRef.current?.next()}
          hideLeft={hideLeft}
          hideRight={hideRight}
        />
      )}
      <div ref={sliderRef} className="keen-slider pt-4 lg:pt-8">
        {renderMobileSpacer()}
        {renderItems()}
        {renderMobileSpacer()}
      </div>
    </div>
  )

  return isMobile ? content : <Container>{content}</Container>
}
